import { useState, useEffect, createContext, useContext } from 'react'
import type { ReactNode } from 'react'

interface UserTimezoneContextType {
  /** User's timezone (e.g., "America/Los_Angeles") */
  timezone: string
  /** Whether timezone is loaded from user profile */
  isLoaded: boolean
  /** Update the timezone (will also update user profile) */
  setTimezone: (tz: string) => Promise<void>
  /** Format a date in user's timezone */
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
  /** Format time in user's timezone */
  formatTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
  /** Format date and time in user's timezone */
  formatDateTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
  /** Convert a Date to user's timezone */
  toUserTimezone: (date: Date | string) => Date
}

// Default to browser's timezone
const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

const UserTimezoneContext = createContext<UserTimezoneContextType | null>(null)

interface UserTimezoneProviderProps {
  children: ReactNode
}

/**
 * Provider component that fetches and provides user's timezone
 */
export function UserTimezoneProvider({ children }: UserTimezoneProviderProps) {
  const [timezone, setTimezoneState] = useState<string>(getBrowserTimezone())
  const [isLoaded, setIsLoaded] = useState(false)

  // Fetch user's timezone from profile on mount
  useEffect(() => {
    const fetchUserTimezone = async () => {
      try {
        const response = await fetch('/api/profile/personal-info', {
          credentials: 'include'
        })
        
        if (response.ok) {
          const data = await response.json()
          const fromProfile = data?.personal_info?.timezone
          // Only a REAL zone may override what the browser detected. The
          // profile used to answer the literal string "UTC" for every user
          // (a placeholder in personal_info), and this check treated it as
          // truthy — so a user in Los Angeles had their correct, detected
          // zone replaced by UTC on every page load. The API now returns
          // null when it has no genuine answer; this guard means an older
          // API, or a profile still carrying the placeholder, cannot undo
          // browser detection either.
          if (data?.success && typeof fromProfile === 'string'
              && fromProfile.trim() && fromProfile.trim().toUpperCase() !== 'UTC') {
            setTimezoneState(fromProfile.trim())
          }
        }
      } catch (error) {
        console.warn('Could not fetch user timezone, using browser timezone:', error)
      } finally {
        setIsLoaded(true)
      }
    }

    fetchUserTimezone()
  }, [])

  // Update timezone in user profile
  const setTimezone = async (tz: string): Promise<void> => {
    try {
      const response = await fetch('/api/profile/personal-info', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ timezone: tz })
      })

      if (response.ok) {
        setTimezoneState(tz)
      }
    } catch (error) {
      console.error('Failed to update timezone:', error)
      throw error
    }
  }

  // Convert a date string or Date to user's timezone
  const toUserTimezone = (date: Date | string): Date => {
    const d = typeof date === 'string' ? new Date(date) : date
    // Create a new date in user's timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(d)
    const get = (type: string) => parts.find(p => p.type === type)?.value || '0'
    
    return new Date(
      parseInt(get('year')),
      parseInt(get('month')) - 1,
      parseInt(get('day')),
      parseInt(get('hour')),
      parseInt(get('minute')),
      parseInt(get('second'))
    )
  }

  // Format date in user's timezone
  const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
  }

  // Format time in user's timezone
  const formatTime = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
  }

  // Format date and time in user's timezone.
  // timeZoneName is on by default (owner ask 2026-08-30): a timestamp that
  // does not say which zone it is in is not auditable — "3:40 PM" is a
  // different moment to every reader. Yields "Aug 24, 2026, 3:40 PM PDT".
  const formatDateTime = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
  }

  const value: UserTimezoneContextType = {
    timezone,
    isLoaded,
    setTimezone,
    formatDate,
    formatTime,
    formatDateTime,
    toUserTimezone
  }

  return (
    <UserTimezoneContext.Provider value={value}>
      {children}
    </UserTimezoneContext.Provider>
  )
}

/**
 * Hook to access user's timezone and formatting utilities
 */
export function useUserTimezone(): UserTimezoneContextType {
  const context = useContext(UserTimezoneContext)
  
  if (!context) {
    // Fallback for components outside the provider
    const browserTz = getBrowserTimezone()
    return {
      timezone: browserTz,
      isLoaded: false,
      setTimezone: async () => { throw new Error('UserTimezoneProvider not found') },
      formatDate: (date) => {
        const d = typeof date === 'string' ? new Date(date) : date
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      },
      formatTime: (date) => {
        const d = typeof date === 'string' ? new Date(date) : date
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      },
      formatDateTime: (date) => {
        const d = typeof date === 'string' ? new Date(date) : date
        return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
      },
      toUserTimezone: (date) => typeof date === 'string' ? new Date(date) : date
    }
  }
  
  return context
}

/**
 * Get common timezone options for selection dropdowns
 */
export const COMMON_TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
]
